import React, { useMemo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BasketDark, UpArrow } from 'src/assets/images';
import { Button } from 'src/features/common';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import { ShowIf } from 'src/components';
import './basket.scss';

export type BasketProps = {
  numberOfDevices: number;
  basketTotalOnceOff: string;
  basketTotalMonthly: string;
  children: React.ReactFragment;
  onBasketOpened: (isBasketOpen: boolean) => void;
};

const BasketButton = ({ numberOfDevices, toggleShowBasket }) => (
  <Button
    disabled={numberOfDevices === 0}
    buttonStyle={DefaultButtonStyle()
      .hasSecondaryAction()
      .isSmallSize()
      .hasLightMode()
      .build()}
    buttonParams={{
      type: ButtonParamType.NormalButton,
      onClick: () => toggleShowBasket(),
    }}
    containerClass="is-relative"
    className="basket-btn"
  >
    <img src={BasketDark} alt="Basket" />
    {numberOfDevices !== 0 && <aside className="quantity-indicator">{numberOfDevices}</aside>}
  </Button>
);

export const Basket = ({
  numberOfDevices,
  basketTotalOnceOff,
  basketTotalMonthly,
  children,
  onBasketOpened,
}: BasketProps) => {
  const [showBasket, setShowBasket] = useState(false);
  const footerButton = useMemo(
    (): HTMLElement | null => document.getElementById('mobile-footer-secondary-button'),
    [],
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleShowBasket = () => {
    setShowBasket(!showBasket);
    onBasketOpened(!showBasket);
  };

  useEffect(() => {
    if (numberOfDevices === 0) {
      setShowBasket(false);
    }
  }, [numberOfDevices]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowBasket(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  return (
    <article ref={wrapperRef}>
      <ShowIf condition={numberOfDevices > 0}>
        <section id="basket-image" className="is-hidden-mobile">
          <img src={BasketDark} alt="Basket" onClick={toggleShowBasket} />

          <section id="basket-total" className="is-hidden-mobile">
            <span className="text">{numberOfDevices}</span>
          </section>
        </section>
      </ShowIf>

      {showBasket && numberOfDevices > 0 && (
        <section id="basket-container">
          <header>
            <h6>My Basket</h6>
            <img
              src={UpArrow}
              alt="Up"
              className="arrow-image"
              onClick={() => {
                toggleShowBasket();
              }}
            />
          </header>
          <section>
            <section className="title-row is-hidden-tablet">
              <p id="solution">Solution</p>
              <p id="quantity">Qty</p>
              <p id="amount">Amount</p>
            </section>
            {children}
          </section>
          <footer>
            <section className="footer-item">
              <p>Once-off + VAT</p>
              <p id="basket-total">{basketTotalOnceOff}</p>
            </section>
            <section className="footer-item">
              <p>Total monthly + VAT</p>
              <p id="basket-total">{basketTotalMonthly}</p>
            </section>
          </footer>
        </section>
      )}
      {footerButton &&
        createPortal(
          <BasketButton numberOfDevices={numberOfDevices} toggleShowBasket={toggleShowBasket} />,
          footerButton,
        )}
    </article>
  );
};
